// DayRangePicker — a calendar popover where:
//   • 1st click sets the start of the range (and the end == start so a
//     single day is a valid one-day "range").
//   • Clicking the SAME selected day toggles it off (clears the range).
//   • Clicking a DIFFERENT day extends/replaces the range:
//       - if no range yet → set as start
//       - if a single-day range exists → use the new day as the
//         opposite bound (auto-orders earliest..latest).
//       - if a multi-day range exists → start a new range from that day.
//
// The component returns ISO local-datetime strings ("YYYY-MM-DDTHH:mm")
// matching the existing API contract: from = day 00:00, to = day 23:59.
// Month navigation comes free with react-day-picker; we expose a 2-month
// view so multi-week / cross-month ranges are easy to set with two
// clicks.
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarDays, X } from 'lucide-react';

const fmt = (iso) => {
  if (!iso) return '';
  // Accept "YYYY-MM-DDTHH:mm" or just "YYYY-MM-DD"
  const d = iso.split('T')[0];
  const [y, m, dd] = d.split('-');
  return `${dd}-${m}-${y}`;
};

const toLocalDate = (iso) => {
  if (!iso) return null;
  const d = iso.split('T')[0].split('-').map(Number);
  return new Date(d[0], d[1] - 1, d[2]);  // local midnight
};

const toIso = (date) => {
  // Format a Date as YYYY-MM-DD (no timezone shift surprises).
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const DayRangePicker = ({
  fromIso, toIso: toIsoVal,
  onChange,        // (fromIso, toIso) => void   ISO datetime "YYYY-MM-DDTHH:mm"
  label,
  testIdPrefix = 'day-range',
  placeholder = 'Pick day(s)…',
  numberOfMonths = 2,
}) => {
  const [open, setOpen] = useState(false);

  const range = useMemo(() => {
    const f = toLocalDate(fromIso);
    const t = toLocalDate(toIsoVal);
    if (!f && !t) return undefined;
    return { from: f || t, to: t || f };
  }, [fromIso, toIsoVal]);

  const triggerLabel = useMemo(() => {
    if (!range) return placeholder;
    if (sameDay(range.from, range.to)) return fmt(toIso(range.from));
    return `${fmt(toIso(range.from))} → ${fmt(toIso(range.to))}`;
  }, [range, placeholder]);

  const handleDayClick = (day /* Date */) => {
    if (!range) {
      // First click: single-day "range" so the user can confirm a
      // one-day filter without extra ceremony.
      const iso = toIso(day);
      onChange?.(`${iso}T00:00`, `${iso}T23:59`);
      return;
    }
    const oneDay = sameDay(range.from, range.to);
    if (oneDay && sameDay(day, range.from)) {
      // Click same selected day → toggle off.
      onChange?.('', '');
      return;
    }
    if (oneDay) {
      // Extend to a real range — auto-order so swapping click order
      // doesn't matter to the user.
      const a = range.from <= day ? range.from : day;
      const b = range.from <= day ? day : range.from;
      onChange?.(`${toIso(a)}T00:00`, `${toIso(b)}T23:59`);
      return;
    }
    // Multi-day range exists → start a new selection from this day.
    const iso = toIso(day);
    onChange?.(`${iso}T00:00`, `${iso}T23:59`);
  };

  const clear = (e) => {
    e?.stopPropagation();
    onChange?.('', '');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-9 px-3 justify-between gap-2 min-w-[230px] font-normal ${range ? '' : 'text-muted-foreground'}`}
          data-testid={`${testIdPrefix}-trigger`}
        >
          <span className="inline-flex items-center gap-2 truncate">
            <CalendarDays className="h-4 w-4 opacity-60 shrink-0" />
            <span className="truncate">{triggerLabel}</span>
          </span>
          {range && (
            <X
              className="h-3.5 w-3.5 opacity-60 hover:opacity-100"
              onClick={clear}
              data-testid={`${testIdPrefix}-clear`}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testIdPrefix}-popover`}>
        {label && (
          <p className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        )}
        <Calendar
          mode="range"
          numberOfMonths={numberOfMonths}
          selected={range}
          weekStartsOn={1}      /* Mon-first to match Dutch / EU planning */
          onDayClick={handleDayClick}
          defaultMonth={range?.from || new Date()}
          data-testid={`${testIdPrefix}-calendar`}
        />
        <div className="flex items-center justify-between border-t px-3 py-2 text-[11px] text-muted-foreground">
          <span>Click again to deselect · click another day to set a range.</span>
          {range && (
            <Button variant="ghost" size="sm" className="h-6 text-[11px]" onClick={clear} data-testid={`${testIdPrefix}-reset`}>
              <X className="h-3 w-3 mr-1" /> Reset
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
